/*! @wharfkit/transact-plugin-autocorrect v4.0.2 | BSD-3-Clause | Copyright (c) 2023 Greymass Inc. | https://github.com/wharfkit/js */
import { AbstractTransactPlugin, Action, Asset, Int64, Name, Struct, TransactHookTypes, UInt32, prependAction } from "@wharfkit/session";
import { Resources } from "@wharfkit/resources";
//#region src/exception.ts
function getException(response) {
	if (response.error) return response.error;
	if (response.processed.except) return response.processed.except;
	return null;
}
//#endregion
//#region \0@oxc-project+runtime@0.147.0/helpers/esm/decorate.js
function __decorate(decorators, target, key, desc) {
	var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
	if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
	else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
	return c > 3 && r && Object.defineProperty(target, key, r), r;
}
//#endregion
//#region src/types.ts
let Powerup = class Powerup extends Struct {};
__decorate([Struct.field(Name)], Powerup.prototype, "payer", void 0);
__decorate([Struct.field(Name)], Powerup.prototype, "receiver", void 0);
__decorate([Struct.field(UInt32)], Powerup.prototype, "days", void 0);
__decorate([Struct.field(Int64)], Powerup.prototype, "net_frac", void 0);
__decorate([Struct.field(Int64)], Powerup.prototype, "cpu_frac", void 0);
__decorate([Struct.field(Asset)], Powerup.prototype, "max_payment", void 0);
Powerup = __decorate([Struct.type("powerup")], Powerup);
let Buyrambytes = class Buyrambytes extends Struct {};
__decorate([Struct.field(Name)], Buyrambytes.prototype, "payer", void 0);
__decorate([Struct.field(Name)], Buyrambytes.prototype, "receiver", void 0);
__decorate([Struct.field(UInt32)], Buyrambytes.prototype, "bytes", void 0);
Buyrambytes = __decorate([Struct.type("buyrambytes")], Buyrambytes);
//#endregion
//#region src/translations/index.ts
var translations_default = {
	en: {
		checking: "Checking transaction",
		fee: {
			"title": "Accept Transaction Fee?",
			"body": "Additional resources ({{resource}}) are required for your account to perform this transaction. Would you like to automatically purchase these resources from the network and proceed?",
			"cost": "Cost of {{resource}}"
		},
		resolving: "Resolving transaction"
	},
	ko: {
		checking: "거래 확인",
		fee: {
			"title": "거래 수수료를 수락하시겠습니까?",
			"body": "계정에서 이 거래를 수행하려면 추가 리소스({{resource}})가 필요합니다. 네트워크에서 이러한 리소스를 자동으로 구매하고 계속 진행하시겠습니까?",
			"cost": "{{resource}}의 비용"
		},
		resolving: "트랜잭션 해결"
	},
	"zh-Hans": {
		checking: "正在检查交易",
		fee: {
			"title": "接受交易费用？",
			"body": "您的帐户需要其他资源 （{{resource}}） 才能执行此交易。是否要自动从网络购买这些资源并继续？",
			"cost": "{{resource}} 的成本"
		},
		resolving: "正在解析交易"
	},
	"zh-Hant": {
		checking: "正在檢查交易",
		fee: {
			"title": "接受交易費用？",
			"body": "您的帳戶需要其他資源 （{{resource}}） 才能執行此交易。是否要自動從網路購買這些資源並繼續？",
			"cost": "{{resource}} 的成本"
		},
		resolving: "正在解析交易"
	}
};
//#endregion
//#region src/index.ts
const chains = {
	aca376f206b8fc25a6ed44dbdc66547c36c6c33e3a119ffbeaef943642f0e906: {
		features: [0, 1],
		sampleAccount: "eosio.reserv",
		symbol: Asset.Symbol.from("4,EOS")
	},
	"73e4385a2708e6d7048834fbc1079f2fabb17b3c125b146af438971e90716c4d": {
		features: [0, 1],
		sampleAccount: "eosio.reserv",
		symbol: Asset.Symbol.from("4,EOS")
	},
	"1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4": {
		features: [0, 1],
		sampleAccount: "boost.wax",
		symbol: Asset.Symbol.from("8,WAX")
	}
};
const multiplier = 1.5;
var TransactPluginAutoCorrect = class extends AbstractTransactPlugin {
	constructor(..._args) {
		super(..._args);
		this.id = "transact-plugin-autocorrect";
		this.translations = translations_default;
		this.sample = null;
		this.price = null;
		this.resources = [];
		this.iterations = 0;
	}
	register(context) {
		if (!context.ui) throw new Error("The TransactPluginAutoCorrect plugin requires a UI to be present.");
		context.addHook(TransactHookTypes.beforeSign, async (request, context) => this.run(request, context));
	}
	async run(request, context) {
		if (!context.ui) return { request };
		this.price = null;
		this.resources = [];
		this.iterations = 0;
		const t = context.ui.getTranslate(this.id);
		const checkingPromise = context.ui.prompt({
			title: t("checking", { default: "Checking transaction" }),
			body: "",
			elements: []
		}).catch((error) => {
			if (error) throw error;
			return { request };
		});
		const account = await context.client.v1.chain.get_account(context.permissionLevel.actor);
		const correctedPromise = this.correct(request, context, account);
		const modified = await Promise.race([checkingPromise, correctedPromise]);
		if (modified === request && !this.price) return { request };
		const resources = Array.from(new Set(this.resources)).join("/");
		return context.ui.prompt({
			title: t("fee.title", { default: "Accept Transaction Fee?" }),
			body: t("fee.body", {
				default: "Additional resources ({{resource}}) are required for your account to perform this transaction. Would you like to automatically purchase these resources from the network and proceed?",
				resource: resources
			}),
			elements: [{
				type: "asset",
				data: {
					label: t("fee.cost", {
						default: "Cost of {{resource}}",
						resource: resources
					}),
					value: this.price
				}
			}, { type: "accept" }]
		}).then(() => ({ request: modified })).catch((error) => {
			if (error) throw error;
			return { request };
		});
	}
	async correct(request, context, account) {
		this.iterations++;
		if (this.iterations > 3) throw new Error("Too many iterations. Please report this bug if you see it.");
		const config = chains[String(context.chain.id)];
		if (!config || !context.ui) return request;
		const resources = new Resources({
			api: context.client,
			sampleAccount: config.sampleAccount
		});
		const resolved = await context.resolve(request);
		return context.client.v1.chain.compute_transaction(resolved.transaction).then((response) => {
			const exception = getException(response);
			if (exception && exception.stack && typeof exception.stack[0] === "object") switch (exception.name) {
				case "tx_net_usage_exceeded": {
					const { net_usage } = exception.stack[0].data;
					const needed = net_usage * multiplier;
					if (config.features.includes(1)) return this.powerup(context, resolved, account, resources, 0, needed);
					break;
				}
				case "tx_cpu_usage_exceeded": {
					const { billed, billable } = exception.stack[0].data;
					const needed = (billed - billable) * multiplier;
					if (config.features.includes(1)) return this.powerup(context, resolved, account, resources, needed, 0);
					break;
				}
				case "ram_usage_exceeded": {
					const { available, needs } = exception.stack[0].data;
					const needed = (needs - available) * multiplier;
					if (config.features.includes(0)) return this.buyram(context, resolved, account, resources, needed);
					break;
				}
			}
			return request;
		}).catch((response) => {
			if (response.error) switch (response.error.name) {
				case "tx_net_usage_exceeded": {
					const [, net_usage] = response.error.details[0].message.match(/transaction net usage is too high: (\d+) > (\d+)/);
					const needed = Number(net_usage) * multiplier;
					if (config.features.includes(1)) return this.powerup(context, resolved, account, resources, 0, needed);
					break;
				}
				case "tx_cpu_usage_exceeded": {
					const [, cpu_usage] = response.error.details[0].message.match(/billed CPU time \((\d+) us\) is greater than the maximum billable CPU time for the transaction/);
					const needed = Number(cpu_usage) * multiplier;
					if (config.features.includes(1)) return this.powerup(context, resolved, account, resources, needed, 0);
					break;
				}
				case "ram_usage_exceeded": {
					const [, , needs, has] = response.error.details[0].message.match(/account (\w.+) has insufficient ram; needs (\d+) bytes has (\d+) bytes/);
					const needed = (Number(needs) - Number(has)) * multiplier + 2;
					if (config.features.includes(0)) return this.buyram(context, resolved, account, resources, needed);
					break;
				}
				default: console.log("unknown error", response);
			}
			return request;
		});
	}
	async buyram(context, resolved, account, resources, needed) {
		const config = chains[String(context.chain.id)];
		const ram = await resources.v1.ram.get_state();
		if (!this.sample) this.sample = await resources.getSampledUsage();
		const price = Asset.from(ram.price_per(needed).value, config.symbol);
		if (this.price) this.price.units.add(price.units);
		else this.price = price;
		this.resources.push("RAM");
		const newAction = Action.from({
			account: "eosio",
			name: "buyrambytes",
			authorization: [resolved.signer],
			data: Buyrambytes.from({
				payer: resolved.signer.actor,
				receiver: resolved.signer.actor,
				bytes: needed
			})
		});
		const newRequest = prependAction(resolved.request, newAction);
		return this.correct(newRequest, context, account);
	}
	async powerup(context, resolved, account, resources, cpu, net) {
		const config = chains[String(context.chain.id)];
		const powerup = await resources.v1.powerup.get_state();
		if (!this.sample) this.sample = await resources.getSampledUsage();
		if (cpu < 2500) cpu = 2500;
		if (net < 1e4) net = 1e4;
		const minPriceValue = Number(Asset.fromUnits(1, config.symbol).value);
		let cpuPrice;
		try {
			cpuPrice = Number(powerup.cpu.price_per(this.sample, cpu));
		} catch {
			cpuPrice = cpu > 0 ? minPriceValue : 0;
		}
		let netPrice;
		try {
			netPrice = Number(powerup.net.price_per(this.sample, net));
		} catch {
			netPrice = net > 0 ? minPriceValue : 0;
		}
		const price = Asset.from(cpuPrice + netPrice * multiplier, config.symbol);
		if (this.price) this.price.units.add(price.units);
		else this.price = price;
		this.resources.push("CPU", "NET");
		const newAction = Action.from({
			account: "eosio",
			name: "powerup",
			authorization: [resolved.signer],
			data: Powerup.from({
				payer: resolved.signer.actor,
				receiver: resolved.signer.actor,
				days: 1,
				net_frac: powerup.net.frac(this.sample, net),
				cpu_frac: powerup.cpu.frac(this.sample, cpu),
				max_payment: price
			})
		});
		let modifiedRequest = prependAction(resolved.request, newAction);
		const ram_needed = 410;
		if (Number(account.ram_quota) - Number(account.ram_usage) < ram_needed) {
			const config = chains[String(context.chain.id)];
			const ram = await resources.v1.ram.get_state();
			if (!this.sample) this.sample = await resources.getSampledUsage();
			const ramAction = Action.from({
				account: "eosio",
				name: "buyrambytes",
				authorization: [resolved.signer],
				data: Buyrambytes.from({
					payer: resolved.signer.actor,
					receiver: resolved.signer.actor,
					bytes: ram_needed
				})
			});
			account.ram_quota.add(ram_needed);
			modifiedRequest = prependAction(modifiedRequest, ramAction);
			const ramPrice = Asset.from(ram.price_per(ram_needed).value, config.symbol);
			this.price.units.add(ramPrice.units);
			this.resources.push("RAM");
		}
		return await this.correct(modifiedRequest, context, account);
	}
};
//#endregion
export { TransactPluginAutoCorrect };

//# sourceMappingURL=transact-plugin-autocorrect.m.js.map